// Story 11.1 — Editor store: tabs + activePath. La lógica vive en
// `lib/editor-tabs-reducer.ts` (deps-free, testeada). Esta store solo
// adapta el reducer a Zustand.
// Story 11.2 — extiende con `diagramVisible` y `diagramSize` (panel del
// flow diagram). State per-session, sin localStorage.
// Story 11.4 — agrega `logsCollapsed`, `logsPanelSize` (panel inferior).

import { create } from 'zustand';
import {
  clampDiagramSize,
  clampLogsPanelSize,
  DIAGRAM_DEFAULT_PCT,
  type LayoutPreset,
  LOGS_DEFAULT_PCT,
  presetToLayout,
} from '@/lib/editor-diagram-prefs';
import {
  type EditorTabsState,
  editorTabsReducer,
  initialEditorTabsState,
} from '@/lib/editor-tabs-reducer';

// Re-export para callers que ya importaban de la store.
export {
  clampDiagramSize,
  clampLogsPanelSize,
  DIAGRAM_DEFAULT_PCT,
  DIAGRAM_MAX_PCT,
  DIAGRAM_MIN_PCT,
  LOGS_DEFAULT_PCT,
  LOGS_MAX_PCT,
  LOGS_MIN_PCT,
} from '@/lib/editor-diagram-prefs';

export interface EditorDiagramState {
  diagramVisible: boolean;
  /** Tamaño del panel del diagrama, en porcentaje del contenedor (20..50). */
  diagramSize: number;
}

export interface EditorLogsState {
  /** True para esconder el LogPanel inferior. */
  logsCollapsed: boolean;
  /** Altura del panel de logs, en porcentaje del contenedor (15..50). */
  logsPanelSize: number;
}

export interface EditorState extends EditorTabsState, EditorDiagramState, EditorLogsState {
  openTab(input: { path: string; content: string; readOnly?: boolean }): void;
  closeTab(path: string): void;
  closeOthers(path: string): void;
  closeAll(): void;
  setActive(path: string): void;
  updateContent(path: string, content: string): void;
  markSaved(path: string): void;
  toggleDiagram(): void;
  setDiagramVisible(visible: boolean): void;
  setDiagramSize(pct: number): void;
  toggleLogs(): void;
  setLogsCollapsed(collapsed: boolean): void;
  setLogsPanelSize(pct: number): void;
  applyLayoutPreset(preset: LayoutPreset): void;
  reset(): void;
}

const now = (): number => Date.now();

const initialDiagramState: EditorDiagramState = {
  diagramVisible: true,
  diagramSize: DIAGRAM_DEFAULT_PCT,
};

const initialLogsState: EditorLogsState = {
  logsCollapsed: false,
  logsPanelSize: LOGS_DEFAULT_PCT,
};

export const useEditorStore = create<EditorState>()((set, get) => ({
  ...initialEditorTabsState,
  ...initialDiagramState,
  ...initialLogsState,
  openTab: ({ path, content, readOnly = false }) => {
    set(editorTabsReducer(get(), { type: 'open', path, content, readOnly, now: now() }));
  },
  closeTab: (path) => set(editorTabsReducer(get(), { type: 'close', path })),
  closeOthers: (path) => set(editorTabsReducer(get(), { type: 'close-others', path })),
  closeAll: () => set(editorTabsReducer(get(), { type: 'close-all' })),
  setActive: (path) => set(editorTabsReducer(get(), { type: 'set-active', path, now: now() })),
  updateContent: (path, content) =>
    set(editorTabsReducer(get(), { type: 'update-content', path, content })),
  markSaved: (path) => set(editorTabsReducer(get(), { type: 'mark-saved', path })),
  toggleDiagram: () => set((s) => ({ diagramVisible: !s.diagramVisible })),
  setDiagramVisible: (visible) => set({ diagramVisible: visible }),
  setDiagramSize: (pct) => set({ diagramSize: clampDiagramSize(pct) }),
  toggleLogs: () => set((s) => ({ logsCollapsed: !s.logsCollapsed })),
  setLogsCollapsed: (collapsed) => set({ logsCollapsed: collapsed }),
  setLogsPanelSize: (pct) => set({ logsPanelSize: clampLogsPanelSize(pct) }),
  // P-445 (review 11.4): pasamos el state actual para que el preset solo
  // controle visibilidad y preserve los `*Size` que el user resizeó.
  applyLayoutPreset: (preset) =>
    set((s) =>
      presetToLayout(preset, {
        diagramVisible: s.diagramVisible,
        diagramSize: s.diagramSize,
        logsCollapsed: s.logsCollapsed,
        logsPanelSize: s.logsPanelSize,
      }),
    ),
  reset: () => set({ ...initialEditorTabsState, ...initialDiagramState, ...initialLogsState }),
}));
